import { useContext, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

import { useTrackedRouter } from '@/lib/attribution/useTrackedRouter'
import {
  type CheckoutCountry,
  type CurrencyCode,
  CHECKOUT_COUNTRIES,
  CHECKOUT_GB,
  COUNTRY_CURRENCY,
} from '@/lib/checkout/types'
import { getFirstParamValue } from '@/lib/utils/queryParams'

import { GeoContext } from '../../context/geoContext'

export const isValidCheckoutCountry = (country: string | undefined | null): country is CheckoutCountry => {
  return !!country && (CHECKOUT_COUNTRIES as readonly string[]).includes(country)
}

export type UseCheckoutCountryType = {
  isReady: boolean
  checkoutCountry?: CheckoutCountry
  currency?: CurrencyCode
  queryParamCountry?: CheckoutCountry
  geoLocCountry: string
  defaultCheckoutCountry: CheckoutCountry
}

const GEO_COUNTRY_COOKIE_NAME = 'cGeoCountry'

export const useCheckoutCountry = (): UseCheckoutCountryType => {
  const router = useTrackedRouter()
  const { country } = useContext(GeoContext)
  const [checkoutCountry, setCheckoutCountry] = useState<CheckoutCountry>()
  const [currency, setCurrency] = useState<CurrencyCode>()
  const [queryParamCountry, setQueryParamCountry] = useState<CheckoutCountry>()
  const [isReady, setIsReady] = useState<boolean>(false)
  useCookies([GEO_COUNTRY_COOKIE_NAME])

  useEffect(() => {
    if (!router.isReady) {
      return
    }

    const queryCountry = (getFirstParamValue(router.query, 'country') || '').toUpperCase()

    if (!queryCountry && !country) {
      return
    }

    // First preference is country query string
    // Second preference is geoCountry
    if (isValidCheckoutCountry(queryCountry)) {
      setCheckoutCountry(queryCountry)
      setCurrency(COUNTRY_CURRENCY[queryCountry])
      setQueryParamCountry(queryCountry)
    } else if (isValidCheckoutCountry(country)) {
      setCheckoutCountry(country)
      setCurrency(COUNTRY_CURRENCY[country])
    }

    setIsReady(true)
  }, [country, router.query, router.isReady])

  return {
    isReady,
    checkoutCountry,
    currency,
    queryParamCountry,
    geoLocCountry: country,
    defaultCheckoutCountry: CHECKOUT_GB,
  }
}
